// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import IconMemo from 'components/atoms/icons/IconMemo';
import styles from './index.module.scss';
import { useEffect, useMemo, useState } from 'react';
import {
  getEmissionSiteMemo,
  updateEmissionSiteMemo,
} from 'apis/collect_route';
import { Modal } from 'components/atoms/Modal';
import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { useTranslation } from 'react-i18next';
import { Label } from 'components/atoms/Label';
import Portal from 'components/atoms/Portal';
import { toast } from 'react-toastify';
import {
  selectCollectingRouteSites,
  setRouteSiteHasMemo,
} from 'redux/slices/collectingRoute';
import { useDispatch, useSelector } from 'react-redux';
import { mergeClasses } from 'util/commons';
import { useGetWindowSize } from 'util/hooks/useGetWindowSize';
import { LAYOUT_BREAK_POINTS } from 'util/ConstantValues';

interface SiteMemoProps {
  siteId: string;
  renderer?: (p: { handleOpen: () => void }) => React.ReactNode;
  onMemoUpdateSuccess?: (note?: string) => void;
  hasMemo?: boolean;
  siteName?: string;
}

export const SiteMemo = ({
  siteId,
  renderer,
  onMemoUpdateSuccess,
  hasMemo: propHasMemo,
  siteName: propSiteName,
}: SiteMemoProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState('');
  const dispatch = useDispatch();
  const collectingRouteSites = useSelector(selectCollectingRouteSites);
  const { windowWidth } = useGetWindowSize();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const { siteName, hasMemo } = useMemo(() => {
    const site = collectingRouteSites?.sites?.find((s) => s.id === siteId);

    return {
      siteName: propSiteName || site?.name || '',
      hasMemo: propHasMemo || site?.hasMemo || false,
    };
  }, [siteId, collectingRouteSites, propSiteName, propHasMemo]);

  const handleSubmitMemo = () => {
    updateEmissionSiteMemo({
      siteId,
      memo: note?.trim() || '',
      successCallback: () => {
        setIsOpen(false);

        dispatch(
          setRouteSiteHasMemo({
            siteId,
            hasMemo: note.trim().length > 0,
          })
        );
        onMemoUpdateSuccess?.(note.trim());
        toast.success(t('messages.M_011'));
      },
    });
  };

  const fetchMemoData = () => {
    getEmissionSiteMemo({
      siteId,
      successCallback: (data) => {
        setNote(data?.memo || '');

        dispatch(
          setRouteSiteHasMemo({
            siteId,
            hasMemo: Boolean(data?.memo && data.memo.length > 0),
          })
        );
      },
    });
  };

  useEffect(() => {
    if (siteId && isOpen) {
      fetchMemoData();
    } else {
      setNote('');
    }
  }, [siteId, isOpen]);

  const renderElement = () => {
    if (renderer) {
      return renderer({ handleOpen });
    }

    return (
      <div
        onClick={handleOpen}
        className={mergeClasses(styles.memo, {
          [styles.active]: hasMemo,
        })}
      >
        <IconMemo
          size={windowWidth <= LAYOUT_BREAK_POINTS.MD ? 20 : 24}
          color={hasMemo ? '#dc910e' : '#86ea5c'}
        />
      </div>
    );
  };

  return (
    <>
      {renderElement()}

      {isOpen && (
        <Portal>
          <div
            className={styles.customModal}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Modal>
              <div className={styles.createMemoModal}>
                <div className={styles.header}>
                  <div className={styles.left}>
                    <div className={styles.icon}>
                      <IconMemo size={24} color="#000" />
                    </div>
                    <span className={styles.title}>
                      {t('emission_site_memo_modal.title')}
                    </span>
                  </div>
                  <div className={styles.closeIcon}>
                    <IconCloseTablet onClick={() => setIsOpen(false)} />
                  </div>
                </div>
                <div className={styles.content}>
                  <div className={styles.siteName}>
                    <span className={styles.label}>
                      {t('emission_site_memo_modal.site_name')}
                    </span>
                    <Label className={styles.siteNameLabel} text={siteName} />
                  </div>
                  <textarea
                    maxLength={1000}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>

                <div className={styles.actionBar}>
                  <button
                    className={styles.closeButton}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('common.button.cancel')}
                  </button>
                  <button
                    className={styles.saveButton}
                    onClick={handleSubmitMemo}
                  >
                    {t('common.button.save')}
                  </button>
                </div>
              </div>
            </Modal>
          </div>
        </Portal>
      )}
    </>
  );
};
